import { useState, useEffect } from 'react'

interface ApiState<T> {
  data: T | null
  loading: boolean
  error: string | null
  errorCode?: string | null
  errorService?: string | null
  refetch: () => void
}

/** Safely cast API data to array — prevents .map() crashes */
export function asArray<T>(data: T[] | T | null | undefined): T[] {
  if (Array.isArray(data)) return data
  if (data != null) {
    console.warn('[asArray] Objet reçu là où un tableau était attendu — contrat API/UI désaccordé ?', data)
  }
  return []
}

export function useApi<T>(url: string, options?: RequestInit): ApiState<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [errorService, setErrorService] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let cancelled = false
    // Skip fetch for empty/falsy URLs
    if (!url) {
      setLoading(false)
      setData(null)
      return
    }
    // Only show loading skeleton on first fetch, not on refetches
    if (data === null) setLoading(true)
    setError(null)
    setErrorCode(null)
    setErrorService(null)
    fetch(url, { credentials: 'include', ...options })
      .then(res => {
        if (!res.ok) {
          if (res.status === 401) throw new Error('Unauthorized')
          return res.json().catch(() => ({})).then((body: any) => {
            const err: any = new Error(body.error || (res.status === 403 ? 'Forbidden' : `${res.status}`))
            err.code = body.code
            err.service = body.service
            throw err
          })
        }
        return res.json()
      })
      .then(json => {
        if (cancelled) return
        if (json.success === false || json.error) {
          setError(json.error ?? 'Erreur inconnue')
        } else {
          setData(json.data !== undefined ? json.data : json)
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err.message)
          setErrorCode(err.code ?? null)
          setErrorService(err.service ?? null)
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [url, tick])

  return { data, loading, error, errorCode, errorService, refetch: () => setTick(t => t + 1) }
}
